import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Subject } from "rxjs";

import { MenuItem } from "primeng/api";
import { ButtonModule } from "primeng/button";
import { Menu, MenuModule } from "primeng/menu";
import { RippleModule } from "primeng/ripple";
import { SkeletonModule } from "primeng/skeleton";
import { TableModule } from "primeng/table";
import { TagModule } from "primeng/tag";
import { ToggleSwitchModule } from "primeng/toggleswitch";
import { TooltipModule } from "primeng/tooltip";
import {
  TableSkeletonRowsComponent,
  SkeletonColumnConfig,
  SKELETON_PRESETS,
} from "../../../../shared/components/table-skeleton";

import { SurveyListItem } from "../../interfaces/retention-workflow.interface";
import { ACTION_TRIGGER_DISPLAY_NAMES } from "../../constants/retention-workflow.constants";

@Component({
  selector: "app-retention-workflows-table",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    MenuModule,
    RippleModule,
    SkeletonModule,
    TagModule,
    ToggleSwitchModule,
    TooltipModule,
    TableSkeletonRowsComponent,
  ],
  templateUrl: "./retention-workflows-table.component.html",
  styleUrl: "./retention-workflows-table.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RetentionWorkflowsTableComponent implements OnDestroy {
  @Input() data: SurveyListItem[] = [];
  @Input() loading = false;
  @Input() scrollHeight = "flex";

  @Output() edit = new EventEmitter<SurveyListItem>();
  @Output() view = new EventEmitter<SurveyListItem>();
  @Output() delete = new EventEmitter<SurveyListItem>();
  @Output() duplicate = new EventEmitter<SurveyListItem>();
  @Output() toggleEnabled = new EventEmitter<SurveyListItem>();

  @ViewChild("contextMenu") contextMenu!: Menu;

  private readonly destroy$ = new Subject<void>();

  currentContextItem: SurveyListItem | null = null;
  contextMenuItems: MenuItem[] = [];
  expandedRows: { [key: string]: boolean } = {};
  readonly skeletonColumns: SkeletonColumnConfig[] =
    SKELETON_PRESETS.retentionWorkflows;

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getActionTriggerLabel(trigger: string): string {
    const triggerNum = parseInt(trigger, 10);
    return ACTION_TRIGGER_DISPLAY_NAMES[triggerNum] || trigger;
  }

  getActionTriggerSeverity(trigger: string): "info" | "warn" {
    const triggerNum = parseInt(trigger, 10);
    return triggerNum === 0 ? "warn" : "info"; // Pause = warn, Cancel = info
  }

  formatDate(date: string | null): string {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }

  onRowEdit(item: SurveyListItem): void {
    if (item.hasCustomerAnswer) {
      this.view.emit(item);
    } else {
      this.edit.emit(item);
    }
  }

  onRowView(item: SurveyListItem): void {
    this.view.emit(item);
  }

  onRowDelete(item: SurveyListItem): void {
    this.delete.emit(item);
  }

  onRowDuplicate(item: SurveyListItem): void {
    this.duplicate.emit(item);
  }

  onEnabledToggle(item: SurveyListItem): void {
    this.toggleEnabled.emit(item);
  }

  getRowMenuItems(item: SurveyListItem): MenuItem[] {
    return [
      {
        label: item.hasCustomerAnswer ? "View" : "Edit",
        icon: item.hasCustomerAnswer ? "pi pi-eye" : "pi pi-pencil",
        command: () => this.onRowEdit(item),
      },
      {
        label: "Duplicate",
        icon: "pi pi-copy",
        command: () => this.onRowDuplicate(item),
      },
      {
        separator: true,
      },
      {
        label: "Delete",
        icon: "pi pi-trash",
        command: () => this.onRowDelete(item),
        styleClass: "p-menuitem-danger",
      },
    ];
  }

  toggleRowMenu(event: Event, item: SurveyListItem): void {
    event.stopPropagation();
    this.currentContextItem = item;
    this.contextMenuItems = this.getRowMenuItems(item);
    this.contextMenu.toggle(event);
  }

  trackBySurveyId(index: number, item: SurveyListItem): string {
    return item.survey.id;
  }

  /**
   * Handle row expand event
   */
  onRowExpand(event: { data: SurveyListItem }): void {
    this.expandedRows = { [event.data.survey.id]: true };
  }

  /**
   * Handle row collapse event
   */
  onRowCollapse(event: { data: SurveyListItem }): void {
    delete this.expandedRows[event.data.survey.id];
  }

  /**
   * Toggle row expansion
   */
  toggleRowExpansion(item: SurveyListItem): void {
    if (this.expandedRows[item.survey.id]) {
      delete this.expandedRows[item.survey.id];
    } else {
      this.expandedRows = { [item.survey.id]: true };
    }
  }
}
